import React from "react";

/**
 * Live-vs-close marking — ONE shared source of truth. CIO rule (2026-06-16):
 * distinguish live/intraday values from settled close values by SHAPE + a
 * "LIVE" tag, NEVER by color alone (color is reserved for the stress ramp).
 *
 * LiveBadge  = pulsing dot + "LIVE" (sky, deliberately not green so it can't be
 *              misread as "good"); for intraday values updated on the poll.
 * AsOfBadge  = static hollow dot + "as of close <date>"; for settled values.
 */

export const LiveBadge: React.FC<{
  label?: string;
  title?: string;
  className?: string;
}> = ({ label = "LIVE", title, className = "" }) => (
  <span
    title={title ?? "Live intraday value — situational awareness only"}
    className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-live/15 text-live ring-1 ring-live/30 ${className}`.trim()}
  >
    <span className="relative flex h-1.5 w-1.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-live/70" />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-live" />
    </span>
    {label}
  </span>
);

export const AsOfBadge: React.FC<{
  date?: string | null;
  prefix?: string;
  className?: string;
}> = ({ date, prefix = "as of close", className = "" }) => (
  <span
    title={date ? `Settled close value (${prefix} ${date})` : "Settled close value"}
    className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-subtle/15 text-subtle ring-1 ring-subtle/30 ${className}`.trim()}
  >
    <span className="inline-block h-1.5 w-1.5 rounded-full border border-current" />
    {date ? `${prefix} ${date}` : prefix}
  </span>
);
